/**
 * @file Contributor UI — a2FuelLayout.
 */

import type { RawMat } from "../hooks/useA2Prefill";
import {
  materialIndexForVehicleFlatRow,
  totalVehicleRows,
} from "./a2VehicleLayout";

/** Max material-level fuel types per raw material (Form A2 transport fuel). */
export const A2_MAX_FUELS_PER_RAW_MATERIAL = 3;

const FUEL_CODE = "A2_FUEL_TRANSPORT";

/** Exported const — material Index For Fuel Flat Row. */
export const materialIndexForFuelFlatRow = materialIndexForVehicleFlatRow;

/** Exported function — insert Index For New Fuel At Material. */
export function insertIndexForNewFuelAtMaterial(
  m: number,
  counts: number[]
): number {
  if (m < 0 || m >= counts.length) return totalVehicleRows(counts);
  return (
    counts.slice(0, m).reduce((a, b) => a + Math.max(0, b), 0) +
    Math.max(0, counts[m])
  );
}

/** Exported function — collect Fuel Value Key Prefixes Including Energy. */
export function collectFuelValueKeyPrefixesIncludingEnergy(
  formSubSections: { code?: string; fields: { name: string }[] }[] | undefined
): string[] {
  const fuelSec = formSubSections?.find((s) => s.code === FUEL_CODE);
  const names = fuelSec?.fields?.length
    ? fuelSec.fields.map((f) => f.name)
    : [];
  const energySec = formSubSections?.find(
    (s) => s.code === "A2_EMBODIED_ENERGY"
  );
  const extra = ["embodiedEnergy", "embodiedEnergy_unit"];
  for (const n of extra) {
    if (energySec?.fields?.some((f) => f.name === n) && !names.includes(n)) {
      names.push(n);
    }
  }
  if (!names.includes("embodiedEnergy")) names.push("embodiedEnergy");
  if (!names.includes("embodiedEnergy_unit")) names.push("embodiedEnergy_unit");
  return names;
}

/** Exported function — shift A2Fuel Values For Insert. */
export function shiftA2FuelValuesForInsert(
  prev: Record<string, string>,
  insertAt: number,
  newRowCount: number,
  fieldNames: string[],
  code: string
): Record<string, string> {
  const next = { ...prev };
  const rowTag = `_${code}_row`;

  for (let i = newRowCount - 1; i > insertAt; i--) {
    const prevI = i - 1;
    for (const base of fieldNames) {
      const fromK = `${base}${rowTag}${prevI}`;
      const toK = `${base}${rowTag}${i}`;
      if (fromK in prev) next[toK] = prev[fromK] ?? "";
      else if (next[toK] !== undefined) delete next[toK];
      const fromL = `${fromK}_label`;
      const toL = `${toK}_label`;
      if (fromL in prev) next[toL] = prev[fromL] ?? "";
      else if (next[toL] !== undefined) delete next[toL];
    }
  }

  for (const base of fieldNames) {
    const k = `${base}${rowTag}${insertAt}`;
    next[k] = "";
    next[`${k}_label`] = "";
  }

  return next;
}

/** Remove flat fuel row `removeAt` and shift later rows down; `oldRowCount` is total fuel rows before removal. */
export function shiftA2FuelValuesForRemove(
  prev: Record<string, string>,
  removeAt: number,
  oldRowCount: number,
  fieldNames: string[],
  code: string
): Record<string, string> {
  const newRowCount = oldRowCount - 1;
  if (
    newRowCount < 0 ||
    removeAt < 0 ||
    removeAt >= oldRowCount ||
    fieldNames.length === 0
  ) {
    return prev;
  }

  const next = { ...prev };
  const rowTag = `_${code}_row`;

  for (let i = removeAt; i < newRowCount; i++) {
    const fromI = i + 1;
    for (const base of fieldNames) {
      const fromK = `${base}${rowTag}${fromI}`;
      const toK = `${base}${rowTag}${i}`;
      next[toK] = prev[fromK] ?? "";
      const fromL = `${fromK}_label`;
      const toL = `${toK}_label`;
      if (fromL in prev) next[toL] = prev[fromL] ?? "";
      else delete next[toL];
    }
  }

  const lastOld = oldRowCount - 1;
  for (const base of fieldNames) {
    const k = `${base}${rowTag}${lastOld}`;
    delete next[k];
    delete next[`${k}_label`];
  }

  return next;
}

function resolveMaterialIndexForFuelItem(
  item: Record<string, unknown>,
  normalizedRaw: RawMat[],
  validCatalogRawMaterialIds: Set<number>,
  catalogNameToId: Map<string, number>,
  normalizeKey: (s: string) => string
): number {
  const R = normalizedRaw.length;
  if (R <= 0) return 0;
  const fromItem = item?.rawMaterialId ?? item?.raw_material_id;
  const n = Number(fromItem);
  if (Number.isFinite(n) && n > 0) {
    for (let m = 0; m < R; m++) {
      if (normalizedRaw[m].catalogId === n) return m;
    }
    for (let m = 0; m < R; m++) {
      if (
        validCatalogRawMaterialIds.size > 0 &&
        validCatalogRawMaterialIds.has(n) &&
        normalizedRaw[m].id === n
      ) {
        return m;
      }
    }
    for (let m = 0; m < R; m++) {
      if (normalizedRaw[m].id === n) return m;
    }
  }
  const nameRaw =
    item?.rawMaterialName ?? item?.raw_material_name ?? item?.materialName ?? "";
  const name =
    typeof nameRaw === "string" ? nameRaw.trim() : String(nameRaw ?? "");
  if (name) {
    const byName = catalogNameToId.get(normalizeKey(name));
    if (byName != null) {
      for (let m = 0; m < R; m++) {
        if (normalizedRaw[m].catalogId === byName) return m;
      }
    }
    for (let m = 0; m < R; m++) {
      if (normalizeKey(normalizedRaw[m].name) === normalizeKey(name)) return m;
    }
  }
  return 0;
}

/** From flat A2_Fuel API list (legacy). */
export function buildA2FuelPrefillLayout(
  fuelArr: unknown[] | undefined,
  normalizedRaw: RawMat[],
  validCatalogRawMaterialIds: Set<number>,
  catalogNameToId: Map<string, number>,
  normalizeRawMaterialNameKey: (s: string) => string
): { counts: number[]; flatItems: Record<string, unknown>[] } {
  const R = Math.max(1, normalizedRaw.length);
  if (!fuelArr?.length) {
    return {
      counts: Array(R).fill(1),
      flatItems: Array.from({ length: R }, () => ({})),
    };
  }

  const buckets: Record<string, unknown>[][] = Array.from(
    { length: R },
    () => []
  );

  for (const raw of fuelArr) {
    const item = (raw && typeof raw === "object" ? raw : {}) as Record<
      string,
      unknown
    >;
    const m = resolveMaterialIndexForFuelItem(
      item,
      normalizedRaw,
      validCatalogRawMaterialIds,
      catalogNameToId,
      normalizeRawMaterialNameKey
    );
    buckets[m].push(item);
  }

  const counts = buckets.map((b) =>
    Math.min(A2_MAX_FUELS_PER_RAW_MATERIAL, Math.max(1, b.length))
  );
  const flatItems: Record<string, unknown>[] = [];
  for (let m = 0; m < R; m++) {
    for (let v = 0; v < counts[m]; v++) {
      flatItems.push(buckets[m][v] ?? {});
    }
  }

  return { counts, flatItems };
}

/** Grouped API: `rawMaterialGroups[m].fuels` (material-level transport fuel, max 3). */
export type A2GroupedFuelSlice = { fuels?: unknown[] };

/** Exported function — build A2Fuel Prefill From Grouped. */
export function buildA2FuelPrefillFromGrouped(
  orderedGroups: A2GroupedFuelSlice[],
  R: number
): { counts: number[]; flatItems: Record<string, unknown>[] } {
  const safeR = Math.max(1, R);
  const groups: A2GroupedFuelSlice[] =
    orderedGroups.length >= safeR
      ? orderedGroups.slice(0, safeR)
      : [
          ...orderedGroups,
          ...Array.from({ length: safeR - orderedGroups.length }, () =>
            ({} as A2GroupedFuelSlice)
          ),
        ];

  const counts = groups.map((g) => {
    const n = Array.isArray(g.fuels) ? g.fuels.length : 0;
    return Math.min(
      A2_MAX_FUELS_PER_RAW_MATERIAL,
      Math.max(1, n)
    );
  });
  const flatItems: Record<string, unknown>[] = [];
  for (let m = 0; m < safeR; m++) {
    const fs = Array.isArray(groups[m].fuels) ? groups[m].fuels! : [];
    const c = counts[m];
    for (let fi = 0; fi < c; fi++) {
      const raw = fs[fi];
      flatItems.push(
        raw && typeof raw === "object"
          ? (raw as Record<string, unknown>)
          : {}
      );
    }
  }
  return { counts, flatItems };
}
